// The platform's host for the Transaction Wizard runner (obj_type "U").
//
// The runner itself — the step machine, the frame stack, every step's screen —
// is the shared `WizardRunner` in @twasta/page-runtime/wizard, mounted the same
// way by the downloaded and J2EE runtimes. This file is the platform's half of
// its seam (`WizardHost`, design §10.1): where the model comes from, the
// transport every request rides (the designer screen runtime, with the wizard
// named in each body so the server authorizes it against the WIZARD's grant —
// §12.2), and how a V / D / R / record is drawn, opened, printed or asked
// about on this surface.
//
// `PlatformWizard` is what every platform surface mounts: the Run view's
// wizard screen, the designer's Preview tab, the slide-over a handler='wizard'
// action button opens.

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { InsightVisual } from '@twasta/visual-engine'
import type { VisualDataPayload } from '@twasta/visual-engine'
import { ReportViewer, withFixedParams } from '@twasta/page-runtime/report'
import type { ReportOutputFormat } from '@twasta/page-runtime/report'
import { ObjectDrawer } from '@twasta/page-runtime/txn/screen'
import {
  allSqlModels, columnDbName, columnFormKey, isHidden, openScreenPayloadOf, parseApiError,
} from '@twasta/page-runtime/txn'
import type {
  AssistantActionRequest, OpenActionRequest, TxnRuntime,
} from '@twasta/page-runtime/txn'
import type { TransactionModel } from '@twasta/page-runtime/txn/types'
import { WizardRunner } from '@twasta/page-runtime/wizard'
import type {
  WizardCallOutcome, WizardHost, WizardModel, WizardObjectSpec,
  WizardPrintRequest, WizardRunnerProps, WizardVia,
} from '@twasta/page-runtime/wizard/contract'
import { loadWizardObject } from '@/api/wizards'
import { fetchVisualData } from '@/api/visualData'
import { runReportJob } from '@/api/reportData'
import { makeDmsClient } from '@/api/dms'
import { waitForPrintJob, wizardDevicePrint, type WizardPrintAnswer } from '@/api/printers'
import { makeReportRuntimeAdapter } from '@/pages/designers/report/ReportLivePreviewPane'
import { DashboardLivePreviewPane } from '@/pages/designers/dashboard/DashboardPreviewModal'
import { livePreviewExecute, livePreviewRights } from '../api'
import { makeDesignerScreenRuntime } from './designerScreenRuntime'
import { openAssistantActionModal } from './designerTxnRuntime'
import { loadModel } from './loadModel'
import OpenObjectPane from './OpenObjectPane'

// Lazy: the transaction pane builds the screen runtime this module is reached
// from (a handler='wizard' button), and importing it eagerly would make the
// two modules import each other.
const LivePreviewLazyPane = lazy(() => import('@/pages/livepreview/LivePreviewPage'))

// ── The host ─────────────────────────────────────────────────────────────

export interface PlatformWizardHostOptions {
  /** Draw a V / D / R / record inside the wizard. */
  renderObject?: WizardHost['renderObject']
  /** `open` tools (and the screens' own handler='open' buttons). */
  openObject: (req: OpenActionRequest) => void
  /** handler='wizard' buttons inside the wizard's own screens. */
  openWizard?: TxnRuntime['openWizardAction']
  /** A report shown in the viewer (print `delivery: preview`). */
  previewReport: (req: WizardPrintRequest) => void
  startWizard?: WizardHost['startWizard']
  close?: WizardHost['close']
  openRecord?: WizardHost['openRecord']
}

/**
 * Build the platform `WizardHost` for one project.
 *
 * The model reads go through `load-wizard-object` (a RUN_APP render read) and
 * are cached per object for the host's lifetime: the runner asks for the
 * base model of every frame it pushes, and a wizard-only user holds no grant
 * that would let them read the base transaction any other way.
 */
export function makePlatformWizardHost(
  project: string, opts: PlatformWizardHostOptions,
): WizardHost {
  const wizards = new Map<string, Promise<{ wizard: WizardModel; base: TransactionModel | null }>>()
  // Filled by the first model read (the server names the caller) — before any
  // run starts, so every `{user.id}` / `{user.name}` token sees it.
  let host: WizardHost | null = null
  const bases = new Map<string, TransactionModel>()
  const rights = new Map<string, Promise<{ can_read: boolean; actions: string[] }>>()

  const readWizard = (objName: string) => {
    let hit = wizards.get(objName)
    if (!hit) {
      hit = loadWizardObject(project, objName).then((res) => {
        const baseName = res.wizard.base_object
        if (res.base && baseName) bases.set(baseName.toLowerCase(), res.base)
        if (res.user && host) host.user = res.user
        return res
      })
      // A failed read is not cached: the next attempt may succeed.
      hit.catch(() => wizards.delete(objName))
      wizards.set(objName, hit)
    }
    return hit
  }

  const notify: WizardHost['notify'] = (kind, message) => {
    if (kind === 'success') toast.success(message)
    else if (kind === 'error') toast.error(message)
    else if (kind === 'warning') toast(message, { icon: '⚠️' })
    else toast(message, { icon: 'ℹ️' })
  }

  host = {
    project,
    loadWizard: async (objName) => (await readWizard(objName)).wizard,
    loadTxnModel: async (objName) => {
      const cached = bases.get(objName.toLowerCase())
      if (cached) return cached
      // Not a base this host has seen with a wizard — the ordinary model read
      // (an author, or a model the caller may read on its own grant).
      return loadModel(project, objName)
    },
    screenRuntime: (via: WizardVia) => makeDesignerScreenRuntime(project, {
      wizard: via,
      openObject: opts.openObject,
      openWizard: opts.openWizard,
    }),
    renderObject: opts.renderObject
      ?? ((spec) => <WizardObjectView project={project} spec={spec} />),
    openObject: opts.openObject,
    openAssistant: (req: AssistantActionRequest) => openAssistantActionModal(project, req),
    printReport: (req) => printWizardReport(project, req, { preview: opts.previewReport, notify }),
    // A `sign` step signs the filed document through the repository's own
    // signing endpoints (DMS rights decide).
    dmsClient: (repository: string) => makeDmsClient(project, repository),
    canUse: async (objType, objName, right) => {
      // The same per-object rights the Run view hides buttons by. Advisory:
      // the endpoints still decide. Any failure answers "yes" — a tool that is
      // then refused says why, a tool wrongly hidden says nothing.
      if (!objName) return true
      const key = `${objType}:${objName}`.toLowerCase()
      let hit = rights.get(key)
      if (!hit) {
        hit = livePreviewRights(project, objName)
        rights.set(key, hit)
      }
      try {
        const r = await hit
        const actions = (r.actions || []).map((a) => String(a).toLowerCase())
        if (actions.includes('*')) return true
        const want = String(right || '').toLowerCase()
        if (!want || want === 'view' || want === 'read' || want === 'run') {
          return !!r.can_read || actions.includes(want)
        }
        return actions.includes(want)
      } catch {
        return true
      }
    },
    startWizard: opts.startWizard,
    close: opts.close,
    openRecord: opts.openRecord,
    notify,
  }
  return host
}

// ── Printing ─────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** The rendered PDF straight into the browser's print dialog: loaded into a
 *  hidden frame, printed from there, the frame removed after the dialog. */
function printPdfBlob(blob: Blob): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    const cleanup = () => {
      setTimeout(() => { frame.remove(); URL.revokeObjectURL(url) }, 60_000)
    }
    frame.onload = () => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
        resolve()
      } catch (err) {
        // A browser that will not script its PDF viewer: open the PDF so the
        // person prints it from there.
        window.open(url, '_blank', 'noopener')
        if (err instanceof Error) console.warn('[wizard] print dialog unavailable:', err.message)
        resolve()
      } finally {
        cleanup()
      }
    }
    frame.onerror = () => { cleanup(); reject(new Error('The report could not be loaded for printing')) }
    frame.src = url
    document.body.appendChild(frame)
  })
}

/** Deliver a wizard's print (preview / device / download / print dialog) on
 *  the platform — shared by the Run view host and the website host. */
export async function printWizardReport(
  project: string,
  req: WizardPrintRequest,
  deps: { preview: (req: WizardPrintRequest) => void; notify: WizardHost['notify'] },
): Promise<void> {
  if (req.delivery === 'preview') {
    deps.preview(req)
    return
  }
  if (req.delivery === 'device') {
    // Silent printing to a named label / receipt printer: the platform renders
    // it as this user and queues it for the site's print agent (design §7.5,
    // docs/device_printing.md). A required print waits for the printer's
    // answer; otherwise "queued" is good enough to move on.
    let answer: WizardPrintAnswer
    try {
      answer = await wizardDevicePrint(project, {
        obj_name: req.objName,
        params: req.params,
        output_format: req.outputFormat,
        copies: req.copies,
        printer: req.printer,
        title: req.title,
        ...(req.via ? { wizard: req.via } : {}),
      })
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404 || status === 405 || status === 501) {
        const msg = parseApiError(err).message
        if (req.required) throw new Error(msg || 'Direct printing is not set up for this application.')
        deps.notify('warning', msg || 'Direct printing is not set up for this application.')
        return
      }
      // 502 carries the printer's own words ("Dock Zebra: … unreachable").
      throw new Error(parseApiError(err).message || 'The label could not be printed')
    }
    const name = answer.printer || req.printer || 'the printer'
    if (answer.status === 'printed') {
      deps.notify('success', `${req.title || req.objName} printed on ${name}`)
      return
    }
    if (!req.required) {
      deps.notify('info', `Sent to ${name} — queued`)
      return
    }
    deps.notify('info', `Sent to ${name} — waiting for the printer…`)
    await waitForPrintJob(project, answer.job_id)
    deps.notify('success', `${req.title || req.objName} printed on ${name}`)
    return
  }
  const format = (req.delivery === 'print_dialog' ? 'pdf' : (req.outputFormat || 'pdf')) as ReportOutputFormat
  const out = await runReportJob({
    project, obj_name: req.objName, params: req.params, output_format: format,
  })
  if (out.needsInput || !out.blob) {
    const missing = (out.missingParams || []).join(', ')
    throw new Error(missing
      ? `The report needs a value for ${missing}`
      : 'The report needs more values before it can print')
  }
  if (req.delivery === 'download') {
    downloadBlob(out.blob, out.filename || `${req.objName}.${format === 'excel' ? 'xlsx' : format}`)
    return
  }
  await printPdfBlob(out.blob)
}

// ── Drawing V / D / R / record ───────────────────────────────────────────

/**
 * A context panel / `show` step's object, drawn on the platform.
 *
 * A visual is fetched and drawn here rather than through the designer's Live
 * Preview pane: a panel has no criteria bar of its own (its params ARE its
 * criteria), a `strip` is a one-line KPI readout, and `pick_row` needs the
 * click on a table row — which `InsightVisual` reports through its opt-in
 * `onRowClick`.
 */
export function WizardObjectView({ project, spec }: { project: string; spec: WizardObjectSpec }) {
  if (spec.objType === 'V') return <WizardVisual project={project} spec={spec} />
  if (spec.objType === 'D') {
    return (
      <div style={{ height: spec.layout === 'full' ? '100%' : 420, minHeight: 0 }}>
        <DashboardLivePreviewPane key={spec.refreshKey} project={project}
          objName={spec.objName} initialGlobals={spec.params} />
      </div>
    )
  }
  if (spec.objType === 'R') return <WizardReport key={spec.refreshKey} project={project} spec={spec} />
  return <WizardRecordCard project={project} spec={spec} />
}

const noteStyle = {
  padding: '10px 12px', fontSize: 13, color: 'var(--color-text-secondary)',
} as const

function Spinner() {
  return (
    <div style={{ ...noteStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
    </div>
  )
}

function cellText(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toLocaleString()
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function WizardVisual({ project, spec }: { project: string; spec: WizardObjectSpec }) {
  const [data, setData] = useState<VisualDataPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const paramsKey = JSON.stringify(spec.params || {})
  useEffect(() => {
    let cancelled = false
    setError(null)
    fetchVisualData({ project, obj_name: spec.objName, params: spec.params })
      .then((payload) => { if (!cancelled) setData(payload) })
      .catch((err) => { if (!cancelled) setError(parseApiError(err).message || 'The visual could not be loaded') })
    return () => { cancelled = true }
    // paramsKey stands for spec.params (a fresh object every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, spec.objName, paramsKey, spec.refreshKey])

  if (error) return <div style={noteStyle}>{error}</div>
  if (!data) return <Spinner />
  if (data.needsInput) {
    return <div style={noteStyle}>Waiting for {(data.missingParams || []).join(', ') || 'values'}…</div>
  }
  const [schema, ...rows] = data.jsonData || ([{}] as unknown as VisualDataPayload['jsonData'])
  const columns = Object.keys(schema || {}).filter((c) => c !== 'id')

  if (spec.layout === 'strip') {
    // "On hand 40 · Reserved 12 · Available 28" — the first row, one line.
    const first = rows[0] || {}
    return (
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '6px 10px',
        fontSize: 14, color: 'var(--color-text)',
      }}>
        {rows.length === 0 ? <span style={{ color: 'var(--color-text-secondary)' }}>No data</span>
          : columns.map((c) => (
            <span key={c}>
              <span style={{ color: 'var(--color-text-secondary)' }}>{c} </span>
              <strong>{cellText(first[c])}</strong>
            </span>
          ))}
      </div>
    )
  }

  return (
    <div style={{ height: spec.layout === 'full' ? '100%' : 320, minHeight: 0 }}>
      <InsightVisual
        jsonData={data.jsonData}
        visualLayout={data.visualLayout}
        optionsArr={data.optionsArr}
        truncated={data.truncated}
        rowLimit={data.rowLimit}
        rowCeiling={data.rowCeiling}
        style={{ height: '100%', width: '100%' }}
        onRowClick={spec.onPickRow ? (row) => spec.onPickRow?.(row) : undefined}
      />
    </div>
  )
}

function WizardReport({ project, spec }: { project: string; spec: WizardObjectSpec }) {
  const adapter = useMemo(
    () => withFixedParams(makeReportRuntimeAdapter(project, spec.objName), spec.params || {}),
    // spec.params is re-keyed through refreshKey (the component remounts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, spec.objName],
  )
  return (
    <div style={{ height: spec.layout === 'full' ? '100%' : 420, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ReportViewer project={project} objName={spec.objName} adapter={adapter} />
    </div>
  )
}

/** A read-only card of another transaction's record (`kind: "record"`). */
/** The header form's visible columns in the form's order, with their
 *  headings — what a record card shows instead of raw column keys. */
interface CardColumn { keys: string[]; heading: string }

function cardColumnsOf(model: TransactionModel): CardColumn[] {
  const header = allSqlModels(model)[0]?.sm
  return (header?.columns || [])
    .map(w => w?.column)
    .filter((c): c is NonNullable<typeof c> => !!c && !isHidden(c))
    .map(c => ({
      keys: Array.from(new Set([columnFormKey(c), columnDbName(c)])),
      heading: String(c.heading || c.name || c.db_name || ''),
    }))
}

function WizardRecordCard({ project, spec }: { project: string; spec: WizardObjectSpec }) {
  const [row, setRow] = useState<Record<string, unknown> | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [columns, setColumns] = useState<CardColumn[] | null>(null)
  useEffect(() => {
    let cancelled = false
    loadModel(project, spec.objName)
      .then((m) => { if (!cancelled) setColumns(cardColumnsOf(m)) })
      .catch((err) => {
        // Headings are presentation: without the model the card still shows
        // the record, keyed by column name.
        console.warn('[wizard] record card: model not loaded', spec.objName, err)
        if (!cancelled) setColumns([])
      })
    return () => { cancelled = true }
  }, [project, spec.objName])
  const matchKey = JSON.stringify(spec.match || {})
  useEffect(() => {
    let cancelled = false
    const match = spec.match || {}
    const filters = Object.entries(match).map(([column, value]) => ({ column, op: 'eq', value }))
    if (!filters.length || filters.some((f) => f.value === null || f.value === undefined || f.value === '')) {
      setRow(null)
      return undefined
    }
    setError(null)
    livePreviewExecute(project, spec.objName, 'list', { limit: 1, offset: 0, filters })
      .then((res) => {
        if (cancelled) return
        const rows = ((res as { rows?: Array<Record<string, unknown>> })?.rows) || []
        setRow(rows[0] || null)
      })
      .catch((err) => { if (!cancelled) setError(parseApiError(err).message || 'The record could not be read') })
    return () => { cancelled = true }
    // matchKey stands for spec.match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, spec.objName, matchKey, spec.refreshKey])

  if (error) return <div style={noteStyle}>{error}</div>
  if (row === undefined || columns === null) return <Spinner />
  if (row === null) return <div style={noteStyle}>No matching record.</div>
  const lower: Record<string, [string, unknown]> = {}
  for (const [k, v] of Object.entries(row)) if (!k.startsWith('_')) lower[k.toLowerCase()] = [k, v]
  const entries: Array<[string, unknown]> = columns && columns.length
    ? columns.flatMap(c => {
      const hit = c.keys.map(k => lower[k]).find(Boolean)
      return hit ? [[c.heading || hit[0], hit[1]] as [string, unknown]] : []
    })
    : Object.values(lower)
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', gap: '3px 12px',
      padding: '8px 10px', fontSize: 13.5,
    }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>{k}</span>
          <span style={{ color: 'var(--color-text)', overflowWrap: 'anywhere' }}>{cellText(v)}</span>
        </div>
      ))}
    </div>
  )
}

// ── The mounted wizard ───────────────────────────────────────────────────

export interface PlatformWizardProps {
  project: string
  wizard: string
  inputs?: Record<string, unknown>
  resumeRunId?: string
  presentation?: 'screen' | 'drawer'
  /** The host can navigate (the Run view): `open` tools asking for
   *  'navigate' go there; anything else opens in a drawer over the wizard. */
  onNavigateObject?: (req: OpenActionRequest) => void
  /** handler='wizard' buttons on the wizard's own screens. */
  openWizard?: TxnRuntime['openWizardAction']
  /** `handoff`: the host replaces the wizard (else it is swapped in place). */
  onStartWizard?: (wizard: string, inputs: Record<string, unknown>) => void
  /** finish.then 'close'. Absent → the wizard starts over. */
  onClose?: (result: { outcome: WizardCallOutcome; txnId?: string | null }) => void
  /** finish.then 'open_record'. */
  onOpenRecord?: (objName: string, txnId: string) => void
  /** Every committed change of the run (the designer Preview's inspector). */
  onState?: WizardRunnerProps['onState']
  /** A `fill` wizard: the opening editor's buffer, and where its result goes. */
  seed?: WizardRunnerProps['seed']
  onFill?: WizardRunnerProps['onFill']
}

/**
 * `WizardRunner` with the platform host, plus the slide-overs a wizard opens
 * on this surface (an `open` tool's object, a report preview). The drawers are
 * mounted IN the tree — they draw transaction screens and dashboards that read
 * this app's router and stores.
 */
export function PlatformWizard({
  project, wizard, inputs, resumeRunId, presentation = 'screen',
  onNavigateObject, openWizard, onStartWizard, onClose, onOpenRecord, onState, seed, onFill,
}: PlatformWizardProps) {
  // What is running. A handoff / restart with no host navigation swaps it
  // here; a resume applies only to the run it was asked for.
  type Current = {
    wizard: string; inputs?: Record<string, unknown>; resume?: string; key: number
  }
  const [current, setCurrent] = useState<Current>({ wizard, inputs, resume: resumeRunId, key: 0 })
  const launched = useRef({ wizard, inputs, resumeRunId })
  useEffect(() => {
    const prev = launched.current
    if (prev.wizard === wizard && prev.inputs === inputs && prev.resumeRunId === resumeRunId) return
    launched.current = { wizard, inputs, resumeRunId }
    setCurrent((c) => ({ wizard, inputs, resume: resumeRunId, key: c.key + 1 }))
  }, [wizard, inputs, resumeRunId])
  const restartWith = useCallback((next: string, nextInputs?: Record<string, unknown>) => {
    setCurrent((c) => ({ wizard: next, inputs: nextInputs, key: c.key + 1 }))
  }, [])

  const [drawer, setDrawer] = useState<
    | { kind: 'open'; req: OpenActionRequest; key: string }
    | { kind: 'record'; objName: string; txnId: string; key: string }
    | null
  >(null)
  const [preview, setPreview] = useState<WizardPrintRequest | null>(null)
  const seq = useRef(0)

  // Through refs: the host is built once per project / wizard, and a callback
  // the parent re-creates every render must not rebuild it (the runner would
  // restart its run).
  const cb = useRef({ onNavigateObject, openWizard, onStartWizard, onClose, onOpenRecord })
  cb.current = { onNavigateObject, openWizard, onStartWizard, onClose, onOpenRecord }

  const host = useMemo(() => makePlatformWizardHost(project, {
    openObject: (req) => {
      const nav = cb.current.onNavigateObject
      if (req.presentation === 'navigate' && nav) { nav(req); return }
      seq.current += 1
      setDrawer({ kind: 'open', req, key: `wizard-open-${seq.current}` })
    },
    openWizard: (req) => {
      const fn = cb.current.openWizard
      if (fn) fn(req)
      // Nowhere to navigate: the launched wizard replaces this one in place.
      else restartWith(req.objName, req.inputs)
    },
    previewReport: (req) => setPreview(req),
    startWizard: (next, nextInputs) => {
      const fn = cb.current.onStartWizard
      if (fn) fn(next, nextInputs)
      else restartWith(next, nextInputs)
    },
    close: (result) => {
      const fn = cb.current.onClose
      if (fn) fn(result)
      else setCurrent((c) => ({ wizard: c.wizard, inputs: c.inputs, key: c.key + 1 }))
    },
    openRecord: (objName, txnId) => {
      const fn = cb.current.onOpenRecord
      if (fn) { fn(objName, txnId); return }
      seq.current += 1
      setDrawer({ kind: 'record', objName, txnId, key: `wizard-record-${seq.current}` })
    },
  }), [project, restartWith])

  const closeDrawer = useCallback(() => {
    setDrawer((d) => { if (d?.kind === 'open') d.req.onClosed?.(); return null })
  }, [])

  const previewAdapter = useMemo(() => (preview
    ? withFixedParams(makeReportRuntimeAdapter(project, preview.objName), preview.params || {})
    : null), [project, preview])

  return (
    <div data-tw-wizard-host={current.wizard}
      style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <WizardRunner
        key={`${current.wizard}:${current.key}`}
        host={host}
        wizard={current.wizard}
        inputs={current.inputs}
        resumeRunId={current.resume}
        presentation={presentation}
        onState={onState}
        // Only the wizard the editor launched fills it (not a handoff / restart).
        {...(current.wizard === wizard && current.key === 0 ? { seed, onFill } : {})}
      />
      {drawer?.kind === 'open' && (
        <ObjectDrawer title={drawer.req.title || drawer.req.objName} objType={drawer.req.objType}
          openMode={drawer.req.mode} onClose={closeDrawer}>
          <OpenObjectPane
            project={project}
            payload={openScreenPayloadOf(drawer.req)}
            openKey={drawer.key}
            layoutMode="customize"
          />
        </ObjectDrawer>
      )}
      {drawer?.kind === 'record' && (
        <ObjectDrawer title={drawer.objName} objType="T" onClose={closeDrawer}>
          <Suspense fallback={<Spinner />}>
            <LivePreviewLazyPane
              key={drawer.key}
              project={project}
              objName={drawer.objName}
              layoutMode="customize"
              initialScreen={{ mode: 'edit', rec: drawer.txnId }}
            />
          </Suspense>
        </ObjectDrawer>
      )}
      {preview && previewAdapter && (
        <ObjectDrawer title={preview.title || preview.objName} objType="R" onClose={() => setPreview(null)}>
          <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ReportViewer project={project} objName={preview.objName} adapter={previewAdapter} />
          </div>
        </ObjectDrawer>
      )}
    </div>
  )
}

/** A full-height box with a message — what a wizard pane shows while it has
 *  nothing to mount. */
export function WizardNote({ children }: { children: ReactNode }) {
  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, textAlign: 'center', fontSize: 14, color: 'var(--color-text-secondary)',
    }}>
      {children}
    </div>
  )
}
